// ChatWindow.js
import React, { useEffect, useRef, useState, useCallback, useImperativeHandle } from "react";
import { parseAndStyleResponse } from "../../utils/ResponseUtils";
import UserInput from "../chatbot/userInput/UserInput";
import DefaultCard from "../chatbot/messages-handling/serverMessage/DefaultCard";
import { GetStreamingResponse } from "../../utils/GetStreamingResponse";
import GetMessagesFromCosmos from "../../utils/database/GetMessagesFromCosmos";
import useUserDetails from "../../utils/auth/UserDetails";
import Loader from "../general/Loader";
import MessageRenderer from "../chatbot/messages-handling/serverMessage/MessageRenderer";
import { CopyCodeListeners } from "../../utils/CopyCodeListeners";
import { applyCustomClasses } from "../../utils/applyCustomClasses";
import hljs from "highlight.js";
import "highlight.js/styles/github.css";
import RegenerateResponse from "../general/RegenerateResponse";
import "./ChatWindow.css";
import { Footer } from "../general/footer";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// Helper function to strip [THINKING]...[/THINKING] markers and create collapsed section
const parseThinkingFromSaved = (text) => {
  if (!text) return { thinking: "", response: text || "" };
  const thinkingMatch = text.match(/\[THINKING\]([\s\S]*?)\[\/THINKING\]/);
  if (thinkingMatch) {
    const thinking = thinkingMatch[1].trim();
    const response = text.replace(/\[THINKING\][\s\S]*?\[\/THINKING\]\s*/g, "").trim();
    return { thinking, response };
  }
  return { thinking: "", response: text };
};

const createThinkingHtml = (thinking) => {
  if (!thinking) return "";
  const escaped = thinking
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return `<details class="claude-thinking"><summary style="cursor:pointer;font-weight:600;color:#666;user-select:none;display:flex;align-items:center;gap:0.5rem;">View Claude's Thinking</summary><div style="margin-top:0.75rem;color:#555;font-size:0.95rem;white-space:pre-wrap;word-wrap:break-word;line-height:1.5;">${escaped}</div></details>`;
};

// Helper function to check if a string is valid Plotly graph JSON
const isGraphData = (str) => {
  try {
    const parsed = JSON.parse(str);
    return parsed && parsed.data && parsed.layout;
  } catch (error) {
    return false;
  }
};

const ChatWindow = React.forwardRef(
  (
    {
      extension,
      chatId,
      oldChats,
      setOldChats,
      setSelectedSection,
      handleSectionChange,
      selectedModel,
      setSelectedModel,
      models,
      setModels,
      isCreatingChat,
      setIsCreatingChat,
      selectedChatObject,
    },
    ref
  ) => {
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isStreaming, setIsStreaming] = useState(false);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [lastUserInput, setLastUserInput] = useState("");
    const [lastSelectedTools, setLastSelectedTools] = useState([]);
    const messagesEndRef = useRef(null);
    const abortControllerRef = useRef(null);
    const userDetails = useUserDetails();
    const instructions = extension?.extensionInstructions || "";
    const [defaultMessageFlag, setDefaultMessageFlag] = useState(false);
    const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);

    const scrollToBottom = useCallback(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, []);

    const setDefaultMessage = useCallback(() => {
      setMessages([
        {
          type: "server",
          content: DefaultCard,
          suggestionKey: null,
          isDefaultMessage: true,
        },
      ]);
      setDefaultMessageFlag(true);
    }, []);

    const loadMessages = useCallback(
      async (chatId) => {
        setIsLoading(true);
        try {
          if (!userDetails.userId) return;
          const messagesFromDb = await GetMessagesFromCosmos(chatId, userDetails.userId);
          if (!messagesFromDb || messagesFromDb.length === 0) {
            setDefaultMessage();
          } else {
            // For each message, if the server message is valid graph JSON, keep it raw;
            // otherwise, process it into HTML.
            const formattedMessages = messagesFromDb.flatMap((msg) => {
              let content;
              if (isGraphData(msg.serverMessage)) {
                content = msg.serverMessage; // raw JSON for graph
              } else {
                // Parse and strip thinking markers before rendering
                const { thinking, response } = parseThinkingFromSaved(msg.serverMessage);
                const thinkingHtml = createThinkingHtml(thinking);

                // Check for file download JSON in saved message
                if (response.includes("file_download") && response.includes("download_url")) {
                  try {
                    const jsonBlocks = [];
                    let depth = 0, start = -1;
                    for (let i = 0; i < response.length; i++) {
                      if (response[i] === '{') { if (depth === 0) start = i; depth++; }
                      if (response[i] === '}') { depth--; if (depth === 0 && start >= 0) {
                        try { const p = JSON.parse(response.substring(start, i + 1)); if (p.type === "file_download") jsonBlocks.push(p); } catch(e) {}
                        start = -1;
                      }}
                    }
                    let downloadCards = "";
                    for (const parsed of jsonBlocks) {
                      const downloadUrl = parsed.download_url;
                      const previewUrl = parsed.preview_url || downloadUrl;
                      const filename = parsed.filename;
                      const fileType = parsed.file_type === "pptx" ? "Presentation" : parsed.file_type === "xlsx" ? "Spreadsheet" : "Document";
                      const icon = parsed.file_type === "pptx" ? "\uD83D\uDCCA" : parsed.file_type === "xlsx" ? "\uD83D\uDCCA" : "\uD83D\uDCC4";
                      const cont = parsed.content || {};
                      const cardId = `file-card-saved-${Date.now()}-${Math.random().toString(36).substr(2,6)}`;
                      const previewIframeUrl = previewUrl.toLowerCase().endsWith('.pdf')
                        ? `${previewUrl}#toolbar=1&navpanes=0`
                        : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewUrl)}`;

                      let summaryText = "";
                      if (parsed.file_type === "pptx" && cont && cont.slides) summaryText = `${cont.slides.length} slides`;
                      else if (parsed.file_type === "docx" && cont && cont.sections) summaryText = `${cont.sections.length} sections`;
                      else if (parsed.file_type === "xlsx" && cont && cont.sheets) summaryText = `${cont.sheets.length} sheets`;

                      downloadCards += `<div style="padding:1rem;background:#f8f8ff;border-radius:10px;border-left:4px solid #333366;margin:0.5rem 0;"><p style="margin:0 0 0.3rem 0;font-weight:600;color:#333366;font-size:1rem;">${icon} ${fileType} Generated Successfully</p><p style="margin:0 0 0.2rem 0;color:#555;font-size:0.9rem;">${cont.title||filename}</p>${summaryText?`<p style="margin:0 0 0.5rem 0;color:#999;font-size:0.8rem;">${summaryText}</p>`:''}<div style="display:flex;gap:10px;margin-top:8px;"><button class="doassist-preview-btn" data-card-id="${cardId}" style="background:white;color:#333366;border:2px solid #333366;padding:8px 20px;border-radius:6px;font-weight:600;font-size:0.9rem;cursor:pointer;">Preview ${fileType}</button><a href="${downloadUrl}" download="${filename}" target="_blank" style="display:inline-block;background:#333366;color:white;padding:8px 20px;border-radius:6px;text-decoration:none;font-weight:600;font-size:0.9rem;">Download ${fileType}</a></div></div><div id="${cardId}" class="doassist-preview-modal" style="display:none;position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:9999;justify-content:center;align-items:center;"><div style="background:white;width:92%;max-width:960px;height:90vh;border-radius:12px;overflow:hidden;display:flex;flex-direction:column;box-shadow:0 8px 32px rgba(0,0,0,0.25);"><div style="background:#333366;color:white;padding:14px 20px;display:flex;justify-content:space-between;align-items:center;flex-shrink:0;"><div style="display:flex;align-items:center;gap:10px;"><span style="font-size:1.3rem;">${icon}</span><div><div style="font-size:1.1rem;font-weight:700;">${cont.title||filename}</div>${cont.subtitle?`<div style="color:#99cc33;font-size:0.8rem;margin-top:1px;">${cont.subtitle}</div>`:''}</div></div><button class="doassist-modal-close" data-card-id="${cardId}" style="background:none;border:none;color:white;font-size:1.8rem;cursor:pointer;padding:0 6px;line-height:1;">&times;</button></div><div style="flex:1;background:#f0f0f0;"><iframe src="${previewIframeUrl}" style="width:100%;height:100%;border:none;" allowfullscreen="true"></iframe></div><div style="padding:10px 20px;border-top:1px solid #eee;display:flex;justify-content:flex-end;gap:10px;flex-shrink:0;background:white;"><button class="doassist-modal-close" data-card-id="${cardId}" style="background:white;color:#333366;border:2px solid #333366;padding:8px 20px;border-radius:6px;font-weight:600;font-size:0.9rem;cursor:pointer;">Close</button><a href="${downloadUrl}" download="${filename}" target="_blank" style="display:inline-block;background:#333366;color:white;padding:8px 20px;border-radius:6px;text-decoration:none;font-weight:600;font-size:0.9rem;">Download</a></div></div></div>`;
                    }
                    content = thinkingHtml + downloadCards;
                  } catch (e) {
                    content = thinkingHtml + applyCustomClasses(parseAndStyleResponse(response));
                  }
                } else {
                  content = thinkingHtml + applyCustomClasses(parseAndStyleResponse(response));
                }
              }
              return [
                { type: "user", content: msg.userMessage, isDefaultMessage: false },
                {
                  type: "server",
                  content: content,
                  suggestionKey: msg.suggestionKey,
                  citations: msg.citations,
                  isDefaultMessage: false,
                },
              ];
            });
            setMessages(formattedMessages);
          }
        } catch (error) {
          console.error("Error loading messages:", error);
        } finally {
          setIsLoading(false);
          setIsCreatingChat(false);
        }
      },
      [userDetails, setDefaultMessage, setIsCreatingChat]
    );

    useEffect(() => {
      setMessages([]);
      if (chatId) {
        loadMessages(chatId);
      } else {
        setIsLoading(false);
      }
    }, [chatId, loadMessages]);

    useEffect(() => {
      scrollToBottom();
      CopyCodeListeners();
      document.querySelectorAll("pre code").forEach((block) => {
        hljs.highlightBlock(block);
      });
      // Attach preview modal listeners for file download cards
      document.querySelectorAll(".doassist-preview-btn").forEach((btn) => {
        if (btn.dataset.listenerAdded) return;
        btn.dataset.listenerAdded = "true";
        btn.addEventListener("click", () => {
          const modal = document.getElementById(btn.dataset.cardId);
          if (modal) modal.style.display = "flex";
        });
      });
      document.querySelectorAll(".doassist-modal-close").forEach((btn) => {
        if (btn.dataset.listenerAdded) return;
        btn.dataset.listenerAdded = "true";
        btn.addEventListener("click", () => {
          const modal = document.getElementById(btn.dataset.cardId);
          if (modal) modal.style.display = "none";
        });
      });
      document.querySelectorAll(".doassist-preview-modal").forEach((modal) => {
        if (modal.dataset.listenerAdded) return;
        modal.dataset.listenerAdded = "true";
        modal.addEventListener("click", (e) => {
          if (e.target === modal) modal.style.display = "none";
        });
      });
      if (messages.length > 1) {
        setDefaultMessageFlag(false);
      }
    }, [messages, scrollToBottom]);

    const processMessage = async (
      input = "",
      suggestionKey = null,
      fileInput = null,
      extName = "Donyati Ask",
      documentIds = [],
      selectedTools = []
    ) => {
      const userInputValue = input.trim();
      if (!userInputValue) return;
      setMessages((prev) => prev.filter((msg) => !msg.isDefaultMessage));

      const suggestionKeyToUse = Array.isArray(suggestionKey)
        ? suggestionKey.map((t) => t.name).join(", ")
        : suggestionKey;

      const newMessage = { type: "user", content: userInputValue, isDefaultMessage: false };
      setMessages((prev) => [...prev, newMessage]);
      setLastUserInput(userInputValue);
      setIsError(false);
      setErrorMessage("");
      setDefaultMessageFlag(false);
      setLastSelectedTools(selectedTools);

      try {
        const updatedTitle = await GetStreamingResponse(
          userInputValue,
          suggestionKeyToUse,
          setMessages,
          setIsStreaming,
          abortControllerRef,
          scrollToBottom,
          fileInput,
          userDetails,
          chatId,
          documentIds,
          selectedTools,
          instructions,
          extension.id,
          selectedModel
        );
        if (updatedTitle) {
          setOldChats((prevChats) =>
            prevChats.map((c) =>
              c.chatId === chatId ? { ...c, chatName: updatedTitle } : c
            )
          );
        }
      } catch (error) {
        setIsError(true);
        setErrorMessage(error.message);
        setDefaultMessageFlag(false);
      }
    };

    const retryProcessMessage = async () => {
      if (!lastUserInput) return;
      try {
        setIsError(false);
        setErrorMessage("");
        await GetStreamingResponse(
          lastUserInput,
          null,
          setMessages,
          setIsStreaming,
          abortControllerRef,
          scrollToBottom,
          null,
          userDetails,
          chatId,
          [],
          lastSelectedTools,
          instructions,
          extension.id,
          selectedModel
        );
      } catch (error) {
        setIsError(true);
        setErrorMessage(error.message);
      }
    };

    const stopStreaming = () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setIsStreaming(false);
    };

    const generatePDF = async () => {
      const messagesContainer = document.getElementById("messages-chat-bot-window");
      if (!messagesContainer) return;

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerHeight = 20;
      const footerHeight = 10;
      const contentHeight = pageHeight - headerHeight - footerHeight - 4;

      const canvas = await html2canvas(messagesContainer, { scale: 2 });
      const imgWidth = pageWidth - 2 * margin;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const totalPages = Math.ceil(imgHeight / contentHeight);

      let heightLeft = imgHeight;
      let position = headerHeight + 4;
      let pageNumber = 1;

      const addHeaderFooter = (pdf, pageNumber) => {
        const currentDate = new Date().toLocaleDateString();
        pdf.setFontSize(10);
        pdf.setTextColor(50);
        pdf.text("DOASSIST", margin, 15);
        pdf.text(`Date: ${currentDate}`, pageWidth - margin - 30, 15);
        pdf.setDrawColor(150);
        pdf.line(margin, 18, pageWidth - margin, 18);
        pdf.line(margin, pageHeight - footerHeight - 3, pageWidth - margin, pageHeight - footerHeight - 3);
        pdf.text("Donyati India Pvt Limited ©", margin, pageHeight - 5);
        pdf.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - margin - 20, pageHeight - 5);
      };

      let currentHeight = 0;

      while (heightLeft > 0) {
        if (pageNumber > 1) pdf.addPage();
        addHeaderFooter(pdf, pageNumber);

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = (contentHeight * canvas.width) / imgWidth;
        const ctx = pageCanvas.getContext("2d");

        ctx.drawImage(
          canvas,
          0,
          currentHeight,
          canvas.width,
          pageCanvas.height,
          0,
          0,
          pageCanvas.width,
          pageCanvas.height
        );

        const pageImgData = pageCanvas.toDataURL("image/png");
        pdf.addImage(pageImgData, "PNG", margin, position, imgWidth, contentHeight);

        heightLeft -= contentHeight;
        currentHeight += pageCanvas.height;
        pageNumber++;
      }

      pdf.save("chat_history.pdf");
    };

    useImperativeHandle(ref, () => ({
      generatePDF,
    }));

    return (
      <div id="chat-window">
        {isLoading || isCreatingChat ? (
          <Loader />
        ) : (
          <>
            <div id="messages-container">
              {defaultMessageFlag ? (
                <div className="default-wrapper">
                  <DefaultCard
                    setSelectedSection={setSelectedSection}
                    handleSectionChange={handleSectionChange}
                  />
                </div>
              ) : null}
              <div id="messages-chat-bot-window">
                {messages.map((msg, index) => {
                  const isLastServerMsg = msg.type === "server" && index === messages.length - 1;
                  const isUserMsg = msg.type === "user";
                  return (
                    <MessageRenderer
                      key={index}
                      type={msg.type}
                      content={msg.content}
                      suggestionKey={msg.suggestionKey}
                      citations={msg.citations}
                      isStreaming={isStreaming && isLastServerMsg}
                      onRegenerate={isLastServerMsg && !isStreaming ? retryProcessMessage : undefined}
                      onEdit={isUserMsg && !isStreaming ? (newText) => {
                        // Remove this message and all messages after it, then resend
                        setMessages((prev) => prev.slice(0, index));
                        processMessage(newText, null, null, extension?.name, [], lastSelectedTools);
                      } : undefined}
                    />
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {isError && (
              <RegenerateResponse onRetry={retryProcessMessage} errorMessage={errorMessage} />
            )}

            <div id="user-input-container">
              <UserInput
                processMessage={processMessage}
                stopStreaming={stopStreaming}
                isStreaming={isStreaming}
                extension={extension}
                chatId={chatId}
                setSelectedDocumentIds={setSelectedDocumentIds}
                selectedDocumentIds={selectedDocumentIds}
                extensionTools={extension?.tools || []}
                customTools={extension?.customTools || []}
                oldChats={oldChats}
                setOldChats={setOldChats}
                selectedChatObject={selectedChatObject}
              />
            </div>
          </>
        )}
        <Footer />
      </div>
    );
  }
);

export default ChatWindow;
